import * as React from "react";
import { useRedraw } from "../models/useRerender";
import Device from "../models/Device";
import { noItx } from "../translationItx/itx";

function isPlainObject(value: any): boolean {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function formatInlineAttributes(device: Device, value: any): string {
  if (!isPlainObject(value)) return "";

  const entries = Object.entries(value).filter(([, v]) => v !== undefined);
  if (entries.length === 0) return "";

  const parts = entries.map(([key, v]) => `${key}: ${JSON.stringify(v)}`);
  const res1 = ` { ${parts.join(", ")} }`;

  let res2 = "";

  if (value.ParamRefId) {
    const v = device.knxInstanceData.getValue(value.ParamRefId, "");
    res2 = ` Value=${v}`;
  }

  return res1 + res2;
}

function getChildContainer(node: any): Record<string, any> {
  if (!isPlainObject(node)) return {};

  if (isPlainObject(node.items)) {
    return node.items;
  }

  return Object.fromEntries(Object.entries(node).filter(([k, v]) => k !== "a" && k !== "items" && v !== undefined));
}

function getLabel(node: any): JSX.Element {
  const name = node["#name"];

  if (node.a) {
    const id = node.a.Id || node.a.RefId || node.a.ParamRefId || node.a.test;

    if (id) {
      return (
        <>
          {`${name} `} <span style={{ color: "grey", padding: "5px", fontSize: "14px" }}>{id}</span>
          {" / "}
        </>
      );
    }
  }
  return (
    <>
      {name} {" / "}
    </>
  );
}

function updateNodeVisibility(node: any, queryLower: string, excludeLower: string): boolean {
  if (!node) return false;

  let found = !queryLower;

  for (let i in node?.a) {
    const attr = node.a[i];
    if (typeof attr == "string") {
      if ((attr as string).toLowerCase().indexOf(queryLower) >= 0) {
        if (!excludeLower || (node["#name"] && node["#name"].toLowerCase().indexOf(excludeLower) < 0)) {
          if (!excludeLower || (attr as string).toLowerCase().indexOf(excludeLower) < 0) {
            found = true;
            break;
          }
        }
      }
    }
  }

  node._found = found;

  for (let i in node.items) {
    const childNode = node.items[i];
    if (updateNodeVisibility(childNode, queryLower, excludeLower)) {
      found = true;
    }
  }

  node._visible = found;

  return found;
}

function Row(props: {
  device: Device;
  name: string;
  node: any;
  parentPath: Array<JSX.Element>;
  setCurrentPath: (newPath: Array<JSX.Element>) => void;
}) {
  const { device, node, parentPath, setCurrentPath, name } = props;

  const [isOpen, setIsOpen] = React.useState(false);
  const isBranch = node.items || node.length > 0;

  const thisPath = [...parentPath, getLabel(node)];

  let childElements: JSX.Element[] = [];
  if (isBranch && isOpen && node._visible !== false) {
    if (node.items) {
      for (let i in node.items) {
        const childNode = node.items[i];
        if (childNode._visible !== false) {
          childElements.push(
            <Row
              device={device}
              name={childNode["#name"]}
              key={i}
              node={childNode}
              parentPath={thisPath}
              setCurrentPath={setCurrentPath}
            />,
          );
        }
      }
      // return childElements;
    }
  }

  return (
    <>
      <div
        onClick={() => setCurrentPath(thisPath)}
        style={{
          marginLeft: `${14}px`,
          cursor: "default",
          backgroundColor: node._found ? "rgba(0,123,255,0.18)" : "transparent",
          borderRadius: "2px",
          padding: "1px 2px",
          userSelect: "text",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <button
          className="button circle small"
          style={{
            width: "20px",
            height: "20px",
            padding: "0 4px",
            marginRight: "10px",
            lineHeight: 1,
          }}
          onClick={(ev) => {
            ev.stopPropagation();
            setIsOpen(!isOpen);
          }}
        >
          {isBranch ? <span>{isOpen ? "▾" : "▸"}</span> : "-"}
        </button>
        <span>{`${name}${formatInlineAttributes(device, node.a)}`}</span>
      </div>

      <div className="estadaColumn left" style={{ marginLeft: "10px" }}>
        {childElements}
      </div>
    </>
  );
}

export function formatKnxRawProductDataText(device: Device, value: any): string {
  const renderEntry = (name: string, node: any, depth: number, lines: string[]) => {
    const indent = "  ".repeat(depth);

    if (Array.isArray(node)) {
      for (const item of node) {
        renderEntry(name, item, depth, lines);
      }
      return;
    }

    if (!isPlainObject(node)) {
      lines.push(`${indent}${name}: ${JSON.stringify(node)}`);
      return;
    }

    lines.push(`${indent}${name}${formatInlineAttributes(device, node.a)}`);

    const childContainer = getChildContainer(node);
    for (const [childName, childValue] of Object.entries(childContainer)) {
      renderEntry(childName, childValue, depth + 1, lines);
    }
  };

  if (!isPlainObject(value)) {
    return JSON.stringify(value, null, 2);
  }

  const lines: string[] = [];
  for (const [name, node] of Object.entries(value)) {
    renderEntry(name, node, 0, lines);
  }

  return lines.join("\n");
}

export default function KnxRawProductDataViewer(props: { device: Device; applicationProgramChildren: any }) {
  const { applicationProgramChildren, device } = props;
  const query = React.useRef("");
  const exclude = React.useRef("");
  const redraw = useRedraw();

  const [current, setCurrentPath] = React.useState<JSX.Element[]>([]);

  const rowElements: any = [];

  for (let i in applicationProgramChildren.items) {
    const node = applicationProgramChildren.items[i];
    if (node._visible !== false) {
      rowElements.push(
        <Row
          device={device}
          name={node["#name"]}
          key={i}
          node={node}
          parentPath={[]}
          setCurrentPath={setCurrentPath}
        />,
      );
    }
  }

  return (
    <div
      className="estadaColumn left w100 h100"
      style={{
        textAlign: "left",
        fontSize: "14px",
        height: "500px",
        flexGrow: 1,
      }}
      tabIndex={0}
    >
      <div
        style={{
          width: "1200px",
          marginBottom: "8px",
          padding: "6px",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          backgroundColor: "lightgrey",
          color: "white",
        }}
      >
        <div className="estadaRow left w100" style={{ flexWrap: "wrap" }}>
          {current || "-"}
        </div>
      </div>

      <div className="estadaRow" style={{ marginLeft: "10px" }}>
        <input
          defaultValue={query.current}
          onChange={(ev) => (query.current = ev.target.value)}
          placeholder="Search in tree..." // no itx
          style={{ minWidth: "220px", flex: "1 1 260px" }}
        />
        {noItx("Exclude:")}{" "}
        <input
          defaultValue={exclude.current}
          onChange={(ev) => (exclude.current = ev.target.value)}
          placeholder="Exclude from matching..." // no itx
          style={{ minWidth: "220px", flex: "1 1 260px" }}
        />
        <button
          className="button"
          style={{ marginLeft: "10px" }}
          onClick={() => {
            updateNodeVisibility(
              applicationProgramChildren,
              query.current.toLowerCase(),
              exclude.current.toLowerCase(),
            );
            redraw();
          }}
        >
          !
        </button>
      </div>
      <div
        role="tree"
        aria-label="KNX raw product data tree" // no itx
        style={{ overflow: "auto", width: "100%", maxHeight: "500px", whiteSpace: "nowrap" }}
      >
        {rowElements}
      </div>
    </div>
  );
}
